import { XlsxAnchor } from '../../types/xlsxAnchor';
/**
 * Pure logic for the chat search-results card: grouping, keying and relevance
 * labelling. Split out from the rendering so rewriting the UI could not disturb
 * it — SearchResultsCard.grouping.test.tsx covers this with 18 cases, including
 * the GDS demo replay and the #783 snippet-preference rules.
 */

export interface SearchResult {
  aggregation?: import("../../types/chat").TableAggregation;
  coverageStatus?: string;
  coverageReasons?: string[];
  xlsxAnchor?: XlsxAnchor;
  id: string;
  title: string;
  path: string[];
  type: 'document' | 'web' | 'code' | 'design' | 'article';
  snippet?: string;
  excerpt?: string;
  sourceUrl?: string;
  provider?: string;
  relevance?: number;
  relevanceLabel?: 'high' | 'medium' | 'low';
  rawRelevance?: number;
  relevanceSource?: string;
  citationIndex?: number;
  selected?: boolean;
  pageNumber?: number;
  segmentId?: string;
  sheetName?: string;
  cellRef?: string;
  cellRange?: string;
  tableBlockId?: string;
  tableFactId?: string;
  tableTitle?: string;
  rowKey?: string;
  columnHeader?: string;
  valueText?: string;
  unit?: string;
}

export type RelevanceLabel = 'high' | 'medium' | 'low';
export interface RelevanceBadgeLabels {
  prefix: string;
  high: string;
  medium: string;
  low: string;
}

export const DEFAULT_RELEVANCE_LABELS: RelevanceBadgeLabels = {
  prefix: 'Relevance',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export const resolveRelevanceLabel = (
  score?: number,
  calibratedLabel?: RelevanceLabel,
): RelevanceLabel | undefined => {
  if (calibratedLabel) return calibratedLabel;
  if (score == null) return undefined;
  if (score >= 0.7) return 'high';
  if (score >= 0.4) return 'medium';
  return 'low';
};

export const getRelevanceBadgeLabel = (
  score?: number,
  calibratedLabel?: RelevanceLabel,
  labels: RelevanceBadgeLabels = DEFAULT_RELEVANCE_LABELS,
) => {
  const label = resolveRelevanceLabel(score, calibratedLabel);
  if (!label) return null;
  return `${labels.prefix}: ${labels[label]}`;
};

// Group multiple ``SearchResult`` rows that share the same ``id``
// (= document file id) into one card so the chat panel doesn't show
// the same file name five times when the retriever surfaced five
// segments from it. ``per_document_limit > 1`` (PR #677) and the
// segment neighbor fetch (PR #680) intentionally emit per-segment
// rows so the LLM gets richer evidence; this grouping is purely a
// display concern (Issue #672 follow-up after 2026-05-18 demo).
//
// The highest-relevance segment per file becomes the visible row;
// remaining segments expand under a localized toggle so citation
// indices the LLM emits ([3], [4], [5]) all stay resolvable to a
// clickable list item.
export interface GroupedResult {
  fileId: string;
  primary: SearchResult;
  additional: SearchResult[]; // [] when only one segment matched
}

// Codex P2 round 5 on PR #682: when ``id`` is missing the
// previous fallback was just ``r.title``, which collapses two
// different files sharing a filename (e.g. ``README.md`` in
// different folders) into one card and routes clicks to the
// wrong source. Fold ``path`` into the fallback so the key is
// stable per logical document but distinct across folders.
function fileKey(r: SearchResult): string {
  if (r.type === 'web' && r.sourceUrl) return r.sourceUrl;
  if (r.id) return r.id;
  const pathPart = (r.path ?? []).join('/');
  return pathPart ? `${pathPart}/${r.title ?? ''}` : (r.title ?? '');
}

export function groupResultsByFile(results: SearchResult[]): GroupedResult[] {
  const groups = new Map<string, SearchResult[]>();
  for (const r of results) {
    const key = fileKey(r);
    const existing = groups.get(key);
    if (existing) {
      existing.push(r);
    } else {
      groups.set(key, [r]);
    }
  }
  const grouped: GroupedResult[] = [];
  for (const [fileId, segments] of groups.entries()) {
    // Highest-relevance segment wins the primary slot so the
    // collapsed card shows the strongest evidence first.
    const sorted = [...segments].sort(
      (a, b) => (b.relevance ?? 0) - (a.relevance ?? 0),
    );
    // The primary drives BOTH the collapsed card's preview and the
    // click-through PDF highlight anchor. A segment without snippet
    // text can do neither job — prefer the strongest segment that has
    // text, falling back to the raw top hit (#783 highlight fix).
    const primary = sorted.find(
      (s) => (s.snippet && s.snippet.trim()) || (s.excerpt && s.excerpt.trim()),
    ) ?? sorted[0];
    grouped.push({
      fileId,
      primary,
      additional: sorted.filter((s) => s !== primary),
    });
  }
  // Preserve the original ordering of the first occurrence of each
  // file so the visible row sequence matches the LLM's confidence
  // ordering across files (top file first).
  const firstSeen = new Map<string, number>();
  results.forEach((r, idx) => {
    const key = fileKey(r);
    if (!firstSeen.has(key)) firstSeen.set(key, idx);
  });
  grouped.sort(
    (a, b) => (firstSeen.get(a.fileId) ?? 0) - (firstSeen.get(b.fileId) ?? 0),
  );
  return grouped;
}
